import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { 
  TagIcon, 
  CubeIcon, 
  ScaleIcon, 
  ClockIcon,
  PlusIcon,
  DocumentTextIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const PriceBookDashboard = () => {
  const [stats, setStats] = useState({
    categories: 0,
    items: 0,
    units: 0,
    frequencies: 0,
    activeTiers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch counts from each endpoint
      const [categoriesRes, itemsRes, unitsRes, frequenciesRes] = await Promise.all([
        api.get('/price-book/categories'),
        api.get('/price-book/items'),
        api.get('/price-book/units'),
        api.get('/price-book/frequencies')
      ]);

      // Count active tiers from items
      let activeTiers = 0;
      for (const item of itemsRes.data) {
        if (item.tier_count) {
          activeTiers += parseInt(item.tier_count);
        }
      }

      setStats({
        categories: categoriesRes.data.length,
        items: itemsRes.data.length,
        units: unitsRes.data.length,
        frequencies: frequenciesRes.data.length,
        activeTiers
      });
    } catch (err) {
      console.error('Error fetching price book stats:', err);
      setError('Failed to load price book statistics');
    } finally {
      setLoading(false);
    }
  };

  const dashboardCards = [
    {
      title: 'Categories',
      count: stats.categories,
      icon: TagIcon,
      color: 'bg-blue-500',
      link: '/price-book/categories',
      description: 'Organize items by category'
    },
    {
      title: 'Items',
      count: stats.items,
      icon: CubeIcon,
      color: 'bg-green-500',
      link: '/price-book/items',
      description: 'Manage price book items'
    },
    {
      title: 'Units of Measure',
      count: stats.units,
      icon: ScaleIcon,
      color: 'bg-purple-500',
      link: '/price-book/units',
      description: 'Define measurement units'
    },
    {
      title: 'Billing Frequencies',
      count: stats.frequencies,
      icon: ClockIcon,
      color: 'bg-orange-500',
      link: '/price-book/frequencies',
      description: 'Set billing intervals'
    }
  ];

  const quickActions = [
    {
      title: 'Add New Item',
      description: 'Create a new price book item',
      icon: PlusIcon,
      link: '/price-book/items/add',
      color: 'bg-indigo-600 hover:bg-indigo-700'
    },
    {
      title: 'Add Category',
      description: 'Create a new category',
      icon: TagIcon,
      link: '/price-book/categories/add',
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      title: 'Price Book Reports',
      description: 'Create and manage pricing reports',
      icon: DocumentTextIcon,
      link: '/price-book/reports',
      color: 'bg-green-600 hover:bg-green-700'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Price Book</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your pricing structure, categories, and billing configurations
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="mt-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {dashboardCards.map((card) => (
            <Link
              key={card.title}
              to={card.link}
              className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <dt>
                <div className={`absolute ${card.color} rounded-md p-3`}>
                  <card.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <p className="ml-16 text-sm font-medium text-gray-500 truncate">{card.title}</p>
              </dt>
              <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
                <p className="text-2xl font-semibold text-gray-900">{card.count}</p>
                <div className="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6">
                  <div className="text-sm">
                    <span className="text-gray-600">{card.description}</span>
                  </div>
                </div>
              </dd>
            </Link>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Price Tiers</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.activeTiers}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/price-book/items" className="font-medium text-indigo-700 hover:text-indigo-900">
                View all items and pricing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {quickActions.map((action) => (
            <Link
              key={action.title}
              to={action.link}
              className={`relative group ${action.color} p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-white rounded-lg shadow-sm text-white hover:shadow-md transition-all duration-200`}
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-white bg-opacity-20">
                  <action.icon className="h-6 w-6" aria-hidden="true" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  {action.title}
                </h3>
                <p className="mt-2 text-sm text-white text-opacity-90">
                  {action.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PriceBookDashboard; 